/*
  Munchief section animations.

  This is the static build's js/animations.js (shopify-html/js/animations.js),
  used as-is so the theme animates exactly like the reference site. Three
  adaptations for running inside a live theme are marked "MUNCHIEF PORT"
  below; nothing else was touched.

  Dependencies, vendored into assets/ and loaded before this file by
  snippets/munchief-scripts.liquid: munchief-gsap.js, munchief-scrolltrigger.js,
  munchief-lenis.js.
*/
/* ==========================================================================
   GSAP-powered section animations, shared across every page.

   Requires gsap.min.js (+ ScrollTrigger.min.js for the scroll-reveal) to be
   loaded via CDN before this file — see the <script> tags in each page's
   <head>/body. Every "hidden" starting state is set here in JS, not in CSS,
   so a blocked/slow CDN just means "no animation", never invisible content.
   ========================================================================== */
(function () {
  /* MUNCHIEF PORT: durations, staggers and trigger points below are tightened
     from the static build's. Those were tuned for a standalone one-pager and
     read as sluggish in a store — a long heading spent ~2.5s on its
     per-character stagger alone. Roughly halved throughout, and the triggers
     fire nearer the bottom of the viewport so content has settled by the time
     it is read. */

  /* MUNCHIEF PORT: the layout loads this once, but the theme editor can
     re-inject scripts when a section is re-rendered — never run twice. */
  if (window.munchiefAnimationsReady) return;
  window.munchiefAnimationsReady = true;

  'use strict';

  if (typeof gsap === 'undefined') return;
  if (typeof ScrollTrigger !== 'undefined') gsap.registerPlugin(ScrollTrigger);

  /* Shared Lenis instance, set by initSmoothScroll below — other init
     functions (e.g. initWorldCardLinks' anchor scroll) read this so their
     jumps stay in Lenis' eased/virtual scroll space instead of fighting it
     with a raw native scrollIntoView. Stays null if Lenis didn't load. */
  var lenis = null;

  /* The gsap.ticker callback driving Lenis' raf loop, kept so it can be
     detached if the instance is rebuilt (see initSmoothScroll). */
  var lenisTick = null;

  /* --------------------------------------------------------------------
     Page transition curtain: a full-screen overlay (.page-transition, see
     css/components.css) that slides up and off on load — revealing the
     page — and slides back up to cover the screen before any internal
     link navigates away. This is a static multi-page build (every section
     preview + index.html is a separate .html file, not an SPA route), so
     without this a click between pages is a hard, uncovered reload cut;
     this makes it read as one continuous motion instead. Injected into
     <body> here rather than authored per-page, so every page gets it
     automatically just by loading this shared script.
     -------------------------------------------------------------------- */
  function initPageTransitions() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var overlay = document.createElement('div');
    overlay.className = 'page-transition';
    document.body.appendChild(overlay);

    gsap.set(overlay, { yPercent: 0 });
    gsap.to(overlay, { yPercent: -100, duration: 0.7, ease: 'power2.inOut', delay: 0.05 });

    // Back/forward nav restoring the page from bfcache: snap the curtain
    // out of the way instantly instead of replaying the reveal, since the
    // page never actually reloaded.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) gsap.set(overlay, { yPercent: -100 });
    });

    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      var link = e.target.closest('a[href]');
      if (!link) return;
      if (link.target && link.target !== '_self') return;
      if (link.hasAttribute('download')) return;

      var href = link.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;
      if (href.indexOf('mailto:') === 0 || href.indexOf('tel:') === 0) return;

      var url;
      try {
        url = new URL(href, window.location.href);
      } catch (err) {
        return;
      }
      if (url.origin !== window.location.origin) return;
      // Same-page anchor jump (e.g. a "#reviews" link on the page that
      // already has that id) — let native/Lenis scroll handle it, no curtain.
      if (url.pathname === window.location.pathname && url.hash) return;

      e.preventDefault();
      gsap.set(overlay, { yPercent: 100 });
      gsap.to(overlay, {
        yPercent: 0,
        duration: 0.5,
        ease: 'power2.inOut',
        onComplete: function () {
          window.location.href = url.href;
        }
      });
    });
  }

  /* --------------------------------------------------------------------
     Smooth scroll (Lenis), driven off gsap.ticker so its raf loop stays
     in lockstep with every ScrollTrigger-based animation above/below —
     without this, scroll-triggered reveals can fire a frame or two out
     of sync with the eased scroll position. Skipped entirely (native
     scroll) if Lenis didn't load or the visitor prefers reduced motion.
     -------------------------------------------------------------------- */
  /* MUNCHIEF PORT: the window is not always the scroller. From 990px up
     base.css puts `height: 100dvh; overflow: hidden` on html/body and makes
     .page-wrapper the scroll container (so the theme drawer can slide in
     beside a page that keeps scrolling). Lenis defaults to wrapper: window,
     so on desktop it was calling preventDefault() on every wheel event and
     then scrolling a window that cannot scroll — trackpad and mouse wheel
     did nothing at all, while touch (which Lenis leaves native) still
     worked. Resolve the element that actually scrolls and hand it to both
     Lenis and ScrollTrigger. */
  function getScroller() {
    var wrapper = document.querySelector('.page-wrapper');
    if (!wrapper) return window;

    var docOverflow = window.getComputedStyle(document.documentElement).overflowY;
    var wrapperOverflow = window.getComputedStyle(wrapper).overflowY;
    var docScrolls = docOverflow !== 'hidden' && docOverflow !== 'clip';
    var wrapperScrolls = wrapperOverflow === 'auto' || wrapperOverflow === 'scroll';

    return !docScrolls && wrapperScrolls ? wrapper : window;
  }

  /* The element whose scroll position this page runs on, whatever it is —
     used by the non-Lenis fallback paths below (reduced motion, or Lenis
     failing to load), which would otherwise scroll an unscrollable window. */
  function scrollerElement() {
    var scroller = getScroller();
    return scroller === window ? document.scrollingElement || document.documentElement : scroller;
  }

  function initSmoothScroll() {
    if (typeof Lenis === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var scroller = getScroller();

    /* ScrollTrigger measures against the window unless told otherwise, and
       every trigger in this file is created after this call, so setting the
       default here is enough. Lenis moves the wrapper with a real
       scrollTo(), so ScrollTrigger reads it natively — no scrollerProxy. */
    if (typeof ScrollTrigger !== 'undefined') {
      ScrollTrigger.defaults({ scroller: scroller === window ? undefined : scroller });
    }

    if (scroller === window) {
      lenis = new Lenis();
    } else {
      /* content is only what Lenis watches for size changes (the wrapper's
         own scrollHeight is what it measures), so point it at the element
         that actually grows when lazy images land. */
      lenis = new Lenis({
        wrapper: scroller,
        content: scroller.querySelector('main') || scroller.firstElementChild || scroller
      });
    }

    if (typeof ScrollTrigger !== 'undefined') {
      lenis.on('scroll', ScrollTrigger.update);
    }

    /* Kept so a rebuild below can detach it — two live ticker callbacks
       would each advance the same Lenis instance once per frame. */
    lenisTick = function (time) {
      if (lenis) lenis.raf(time * 1000);
    };
    gsap.ticker.add(lenisTick);
    gsap.ticker.lagSmoothing(0);

    /* Crossing the 990px breakpoint swaps which element scrolls. Rebuild
       Lenis against the new one — its wrapper is fixed at construction. */
    var desktop = window.matchMedia('(min-width: 990px)');
    var onBreakpoint = function () {
      if (getScroller() === lenis.options.wrapper) return;
      gsap.ticker.remove(lenisTick);
      lenis.destroy();
      lenis = null;
      initSmoothScroll();
      if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
    };
    if (desktop.addEventListener) {
      desktop.addEventListener('change', onBreakpoint, { once: true });
    } else if (desktop.addListener) {
      desktop.addListener(onBreakpoint);
    }
  }

  /* --------------------------------------------------------------------
     Splits a heading's text into one <span class="word-reveal__char">
     per non-space character, keeping whitespace as plain text nodes (so
     wrapping still behaves normally — only the letters themselves are
     animated spans). Every section title's reveal runs through this: the
     hero heading below, the footer heading, and the generic
     data-animate="char-reveal" hook.

     It walks the DOM rather than re-parsing innerHTML, which is what lets a
     heading carry inline markup: <br> line breaks survive, and so does an
     inline <span> wrapper — its letters are split IN PLACE, inside the
     span, so a two-tone line (product_story's statement, where the sentence
     changes colour mid-thought) keeps its colours. Re-parsing innerHTML
     turned any such tag into letter spans of its own markup.

     Walking nodes also drops the old entity-decoding step: a text node's
     textContent is already decoded, so an authored "&" arrives as one
     character instead of the five of "&amp;".
     -------------------------------------------------------------------- */
  function splitIntoChars(heading) {
    var chars = [];

    function walk(parent) {
      /* Snapshot: the loop replaces children as it goes. */
      var nodes = Array.prototype.slice.call(parent.childNodes);

      nodes.forEach(function (node) {
        if (node.nodeType === 3) {
          var text = node.textContent;
          if (!text) return;

          var frag = document.createDocumentFragment();
          /* One text node per whitespace run, one span per visible
             character — collapsing each run to a single space keeps the
             authored indentation out of the rendered line. */
          text.split(/(\s+)/).forEach(function (chunk) {
            if (!chunk) return;
            if (/^\s+$/.test(chunk)) {
              frag.appendChild(document.createTextNode(' '));
              return;
            }
            /* Each word gets its own wrapper, which the CSS makes an
               atomic inline-block. The letters are inline-block so they can
               be scaled, which otherwise lets a line break between ANY two
               of them — headings were splitting mid-word ("YO / U IT'S").

               A hyphen is still a legal break point, so a word is cut into
               one wrapper per hyphen-terminated segment: "NON-SODA" becomes
               "NON-" + "SODA" and can wrap between them, exactly where the
               comp breaks it. Written as a loop rather than a lookbehind
               regex, which older Safari throws on at parse time. */
            var segments = [];
            var segment = '';
            chunk.split('').forEach(function (char) {
              segment += char;
              if (char === '-') {
                segments.push(segment);
                segment = '';
              }
            });
            if (segment) segments.push(segment);

            segments.forEach(function (text) {
              var word = document.createElement('span');
              word.className = 'word-reveal__word';
              text.split('').forEach(function (char) {
                var span = document.createElement('span');
                span.className = 'word-reveal__char';
                span.textContent = char;
                word.appendChild(span);
                chars.push(span);
              });
              frag.appendChild(word);
            });
          });

          parent.replaceChild(frag, node);
          return;
        }

        /* <br> and anything else with no text of its own stay as they are;
           an inline wrapper is recursed into so its own letters animate. */
        if (node.nodeType === 1 && node.tagName !== 'BR') walk(node);
      });
    }

    walk(heading);
    return chars;
  }

  /* --------------------------------------------------------------------
     The reveal every title on the site runs through: split the heading to
     characters, then pop each one from scale(0.25) up to full size, one
     after the next. Deliberately a scale rather than the bottom-to-top
     slide these used to do — the letters land in place, so nothing
     reflows mid-reveal and the letters still waiting already sit in their
     final position.

     CHAR_STAGGER is the gap between letters and is what makes the line
     read as one-at-a-time instead of a single flash. A long title at that
     gap would still be animating after the reader has moved on, so the
     run of letters is capped at MAX_SWEEP seconds and the gap tightens on
     headings long enough to need it. MAX_SWEEP plus the per-letter
     duration below is the worst-case wall time of a reveal — both are set
     so that stays under one second no matter how long the heading is.

     Fires off IntersectionObserver rather than ScrollTrigger: most of
     these sit below lazy-loaded images, so a ScrollTrigger start measured
     at DOMContentLoaded is stale by the time the reader arrives and the
     tween silently never runs, leaving the heading at opacity 0. An
     observer measures at intersection time and cannot go stale.

     opts.immediate plays at once instead of waiting to be scrolled to
     (the hero, which is already on screen at load). Returns the tween in
     that case so a caller can chain off its length.
     -------------------------------------------------------------------- */
  var CHAR_STAGGER = 0.04;
  var MAX_SWEEP = 0.65;

  function revealChars(heading, opts) {
    if (!heading) return null;

    var chars = splitIntoChars(heading);
    if (!chars.length) return null;

    opts = opts || {};

    gsap.set(chars, {
      display: 'inline-block',
      opacity: 0,
      scale: 0.25,
      transformOrigin: '50% 60%'
    });

    function play() {
      return gsap.to(chars, {
        opacity: 1,
        scale: 1,
        duration: 0.3,
        ease: 'back.out(1.6)',
        stagger: Math.min(CHAR_STAGGER, MAX_SWEEP / chars.length),
        delay: opts.delay || 0,
        overwrite: 'auto'
      });
    }

    if (opts.immediate || typeof IntersectionObserver === 'undefined') {
      return play();
    }

    new IntersectionObserver(function (entries, obs) {
      if (!entries[0].isIntersecting) return;
      obs.disconnect();
      play();
    }, { threshold: 0.1 }).observe(heading);

    return null;
  }

  /* --------------------------------------------------------------------
     Hero heading: same per-character pop as every other title (see
     revealChars above), but played on load rather than on scroll, since
     it is on screen already. The supporting copy and buttons then come in
     once the letters have nearly finished, so the two don't compete.
     -------------------------------------------------------------------- */
  function animateHeroHeading() {
    var heading = document.querySelector('.hero__heading');
    if (!heading) return;

    var sweep = revealChars(heading, { immediate: true, delay: 0.05 });
    if (!sweep) return;

    var rest = document.querySelectorAll('.hero__text, .hero .btn-group, .hero__secondary');
    if (!rest.length) return;

    gsap.set(rest, { opacity: 0, y: 20 });
    gsap.to(rest, {
      opacity: 1,
      y: 0,
      duration: 0.4,
      ease: 'power3.out',
      stagger: 0.06,
      delay: Math.max(0, sweep.totalDuration() - 0.25)
    });
  }

  /* --------------------------------------------------------------------
     Hero side images: on load, left_image/right_image rise up from below
     into their resting spot (same anchor point CSS already gives them),
     fading in as they arrive. Runs alongside the heading's char reveal,
     not blocking it — both start together.
     -------------------------------------------------------------------- */
  function animateHeroSideImages() {
    var left = document.querySelector('.hero__side--left');
    var right = document.querySelector('.hero__side--right');
    if (!left && !right) return;

    var tl = gsap.timeline({ delay: 0.1 });

    if (left) {
      gsap.set(left, { y: 140, opacity: 0 });
      tl.to(left, { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' }, 0);
    }
    if (right) {
      gsap.set(right, { y: 140, opacity: 0 });
      tl.to(right, { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' }, 0.08);
    }
  }

  /* --------------------------------------------------------------------
     Footer heading ("THC-infused sparkling juices & gummies"): the shared
     per-character pop (see revealChars above), nothing section-specific.
     -------------------------------------------------------------------- */
  function animateFooterHeading() {
    revealChars(document.querySelector('.footer__heading'));
  }

  /* --------------------------------------------------------------------
     Brand story "intro" row ("Crunch it. Sip it. Repeat." + hero photo):
     on scroll into view, the heading slides in from off-screen left and
     the photo slides in from off-screen right, both converging on their
     normal resting position (the photo's own resting rotate(1.85deg) is
     untouched — gsap's x/y are a separate transform component from the
     CSS transform already on the element, so they compose instead of
     replacing it). Scroll-triggered rather than on-load since this row is
     well below the fold (7th section on the home page).
     -------------------------------------------------------------------- */
  /* --------------------------------------------------------------------
     Word-by-word reveal, for body copy. Same split as the per-character
     title reveal (splitIntoChars) — it already wraps each word — but the
     stagger runs over the WORD wrappers, not their letters: a paragraph
     has hundreds of characters and popping each one reads as noise and
     costs hundreds of tweens.

     The sweep is capped the same way revealChars caps its own: the gap
     tightens as the copy gets longer, so a long paragraph never keeps
     animating after the reader has started it.
     -------------------------------------------------------------------- */
  var WORD_STAGGER = 0.03;
  var WORD_SWEEP = 0.6;

  function revealWords(el) {
    if (!el) return null;
    if (!splitIntoChars(el).length) return null;

    var words = el.querySelectorAll('.word-reveal__word');
    if (!words.length) return null;

    var stagger = Math.min(WORD_STAGGER, WORD_SWEEP / words.length);

    gsap.set(words, { opacity: 0, y: 12 });

    function play() {
      gsap.to(words, {
        opacity: 1,
        y: 0,
        duration: 0.4,
        ease: 'power2.out',
        stagger: stagger,
        overwrite: 'auto'
      });
    }

    if (typeof IntersectionObserver === 'undefined') {
      play();
      return null;
    }

    new IntersectionObserver(function (entries, obs) {
      if (!entries[0].isIntersecting) return;
      obs.disconnect();
      play();
    }, { threshold: 0.15 }).observe(el);

    return null;
  }

  function animateBrandStoryIntro() {
    if (typeof ScrollTrigger === 'undefined') return;

    document.querySelectorAll('.brand-story__row--intro').forEach(function (row) {
      var heading = row.querySelector('.brand-story__heading');
      var photo = row.querySelector('.brand-story__photo');
      if (!heading && !photo) return;

      var tl = gsap.timeline({
        scrollTrigger: { trigger: row, start: 'top 80%' }
      });

      if (heading) {
        gsap.set(heading, { x: '-15vw', opacity: 0 });
        tl.to(heading, { x: 0, opacity: 1, duration: 0.55, ease: 'power3.out' }, 0);
      }
      if (photo) {
        gsap.set(photo, { x: '15vw', opacity: 0 });
        tl.to(photo, { x: 0, opacity: 1, duration: 0.55, ease: 'power3.out' }, 0.08);
      }
    });
  }

  /* --------------------------------------------------------------------
     Brand story "about" row (character photo, subheading, body copy, CTA
     pair): reveals one piece at a time, scroll-triggered since the row
     sits below the fold. Unlike the generic fade-up-stagger below, the
     four pieces here aren't direct siblings (heading/text/CTA are nested
     inside .brand-story__content alongside the photo), so this targets
     them explicitly instead of relying on childNodes order.
     -------------------------------------------------------------------- */
  function animateBrandStoryAbout() {
    if (typeof ScrollTrigger === 'undefined') return;

    document.querySelectorAll('.brand-story__row--about').forEach(function (row) {
      var photo = row.querySelector('.brand-story__characters');
      /* The subheading and the paragraph carry their own per-character and
         per-word reveals (data-animate in the section markup), so they are
         deliberately absent here — fading their containers as well would
         hold the whole block at opacity 0 until this timeline reached them,
         swallowing the first half of each sequence. */
      var copy = [
        row.querySelector('.btn-group')
      ].filter(Boolean);
      if (!photo && !copy.length) return;

      var tl = gsap.timeline({
        scrollTrigger: { trigger: row, start: 'top 88%' }
      });

      if (photo) {
        /* Slides in from off-screen left to its resting spot, mirroring the
           intro row's heading. Its CSS rotate(-2.8deg) survives: gsap's x is
           a separate transform component, so the two compose rather than one
           replacing the other. */
        gsap.set(photo, { x: '-15vw', opacity: 0 });
        tl.to(photo, { x: 0, opacity: 1, duration: 0.6, ease: 'power3.out' }, 0);
      }

      if (copy.length) {
        gsap.set(copy, { opacity: 0, y: 28 });
        /* Last in: the CTA lands after the paragraph's own word sweep has
           had time to run, so the row resolves top to bottom. */
        tl.to(copy, {
          opacity: 1,
          y: 0,
          duration: 0.45,
          ease: 'power3.out',
          stagger: 0.12
        }, 0.45);

        /* The offset has to clear revealStuck's 400ms net, or the CTA is read
           as stuck and flat-faded before this timeline reaches it. */
        copy.forEach(function (el, i) {
          el.dataset.revealDelay = Math.round((0.45 + i * 0.12) * 1000);
        });
      }
    });
  }

  /* --------------------------------------------------------------------
     Generic scroll-reveal, opt-in per element via a data attribute:
       data-animate="fade-up"          fades/slides the element itself in
       data-animate="fade-up-stagger"  fades/slides its direct children in,
                                        staggered (use on a card grid/row)
       data-animate="word-reveal"      fades the element's own words in one
                                        after the next (body copy — the
                                        per-character version below is for
                                        titles, and reads as noise on a
                                        paragraph)
       data-animate="char-reveal"      splits the heading's own text into
                                        characters and reveals them one at
                                        a time, same mechanic as the hero/
                                        footer headings (see splitIntoChars
                                        above) — use on every section title
                                        (.stroke-heading etc.) so every
                                        page heading animates in the same
                                        letter-by-letter way
     -------------------------------------------------------------------- */
  /* MUNCHIEF PORT: Shopify wraps every theme block in a <div class="shopify-block">
     that munchief-base.css sets to `display: contents`. That wrapper is the
     direct child of any block container, but it generates no box — opacity and
     transform on it paint nothing, so a stagger written to the wrappers never
     showed. Swap each wrapper for the elements it wraps. */
  function expandBlocks(nodes) {
    var out = [];
    Array.prototype.forEach.call(nodes, function (node) {
      if (node.classList && node.classList.contains('shopify-block')) {
        /* A block also emits its own <style data-shopify> for the merchant
           color overrides. Animating that would burn the first slot of the
           stagger on an invisible element. */
        Array.prototype.forEach.call(node.children, function (child) {
          if (child.tagName === 'STYLE' || child.tagName === 'SCRIPT') return;
          out.push(child);
        });
      } else {
        out.push(node);
      }
    });
    return out;
  }

  function animateOnScroll() {
    /* Ahead of the ScrollTrigger guard below: the character reveal runs off
       IntersectionObserver, so titles still animate on a page where the
       ScrollTrigger plugin failed to load. */
    document.querySelectorAll('[data-animate="char-reveal"]').forEach(function (heading) {
      revealChars(heading);
    });

    /* Observer-driven for the same reason as the character reveal above. */
    document.querySelectorAll('[data-animate="word-reveal"]').forEach(function (el) {
      revealWords(el);
    });

    /* Also ahead of the guard, and on an observer rather than a
       ScrollTrigger, for the reason spelled out on revealChars: these rows
       sit below lazy-loaded images, so a start position measured at
       DOMContentLoaded is stale by the time the reader gets there and the
       tween never fires — which is exactly how the promise cards ended up
       just sitting there. An observer measures at intersection time. */
    document.querySelectorAll('[data-animate="fade-up-stagger"]').forEach(function (group) {
      var items = expandBlocks(group.children);
      if (!items.length) return;

      /* A section can widen the sequence into a visible one-by-one reveal
         with data-animate-stagger (seconds between cards), and tune the
         travel and duration alongside it. The defaults are the original
         near-simultaneous fade, so groups that set nothing are unchanged. */
      var stagger = parseFloat(group.dataset.animateStagger) || 0.05;
      var travel = parseFloat(group.dataset.animateY) || 40;
      var duration = parseFloat(group.dataset.animateDuration) || 0.45;

      gsap.set(items, { opacity: 0, y: travel });

      /* revealStuck below force-shows anything still transparent 400ms after
         it scrolls in — fine for the default stagger, but it would flatten a
         deliberate one-by-one sequence whose last card starts later than
         that. Each card records its own delay so the net waits past it. */
      items.forEach(function (item, i) {
        item.dataset.revealDelay = Math.round(i * stagger * 1000);
      });

      function play() {
        gsap.to(items, {
          opacity: 1,
          y: 0,
          duration: duration,
          ease: 'power2.out',
          stagger: stagger,
          overwrite: 'auto'
        });
      }

      if (typeof IntersectionObserver === 'undefined') {
        play();
        return;
      }

      new IntersectionObserver(function (entries, obs) {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        play();
      }, { threshold: 0.15 }).observe(group);
    });

    if (typeof ScrollTrigger === 'undefined') return;

    document.querySelectorAll('[data-animate="fade-up"]').forEach(function (el) {
      gsap.set(el, { opacity: 0, y: 40 });
      gsap.to(el, {
        opacity: 1,
        y: 0,
        duration: 0.45,
        ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 95%' }
      });
    });

  }

  /* --------------------------------------------------------------------
     Chaos banner -> footer transition: wherever a chaos_banner section
     sits directly above the footer, its content drifts upward slightly
     faster than the page scrolls (scrubbed to scroll position), so the
     footer's wavy top edge (footer.css's .footer::before, which already
     rises 108px into whatever precedes it) reads as sliding up and over
     the banner rather than the two sections just being stacked.
     -------------------------------------------------------------------- */
  /* MUNCHIEF PORT: the two chaos-banner scrubs are driven by a plain rAF
     scroll handler instead of ScrollTrigger's scrub. The values are the
     original's: the content drifts up 18% and the photo travels -10% -> +10%
     across the section's whole time on screen.

     ScrollTrigger stores its start/end positions when the tween is created,
     and this theme lazy-loads images, so the page height keeps changing while
     the visitor scrolls and the scrub silently stopped tracking. Reading the
     live rect every frame cannot go stale. */
  function initChaosScrollScrub() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var banners = Array.prototype.slice.call(document.querySelectorAll('.chaos-banner'));
    if (!banners.length) return;

    /* .chaos-banner__content is centred with translateX(-50%) in CSS, so the
       drift cannot just be written to style.transform — that would replace the
       centring and leave the block hanging off the middle of the section.
       Reading the computed matrix and re-appending to it (what GSAP does) has
       its own failure: the computed value is the -50% already resolved to
       pixels for the width the box happened to have at init, which is before
       webfonts settle and stale after any resize. So the drift travels as a
       custom property instead and chaos-banner.css composes the two, keeping
       the -50% a live percentage. */
    var items = banners.map(function (banner) {
      var image = banner.querySelector('.chaos-banner__media img');
      return {
        banner: banner,
        content: banner.querySelector('.chaos-banner__content'),
        image: image,
        /* The photo carries no transform of its own, so it can keep taking the
           drift directly. */
        imageBase: ''
      };
    });

    function update() {
      var viewport = window.innerHeight;

      items.forEach(function (item) {
        var rect = item.banner.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > viewport) return;

        /* 0 when the section's top meets the bottom of the viewport, 1 when
           its bottom leaves the top — the span the original scrubbed over. */
        var progress = (viewport - rect.top) / (rect.height + viewport);
        progress = Math.min(1, Math.max(0, progress));

        if (item.content) {
          item.content.style.setProperty(
            '--chaos-drift',
            (progress * -18).toFixed(2) + '%'
          );
        }
        if (item.image) {
          item.image.style.transform =
            item.imageBase + 'translateY(' + (progress * 20 - 10).toFixed(2) + '%)';
        }
      });
    }

    /* Driven by a rAF loop that runs only while a banner is on screen, rather
       than by scroll events. Smooth-scroll libraries, inertial touch scrolling
       and browser scroll anchoring all deliver scroll events on their own
       schedule (or, under a virtual scroller, not to window at all); a frame
       loop tracks whatever the page is actually doing. It idles completely
       once the section leaves the viewport. */
    var visible = 0;
    var running = false;

    function frame() {
      if (!visible) {
        running = false;
        return;
      }
      update();
      requestAnimationFrame(frame);
    }

    function start() {
      if (running) return;
      running = true;
      requestAnimationFrame(frame);
    }

    if (typeof IntersectionObserver === 'undefined') {
      /* No observer: fall back to always running the loop. */
      visible = items.length;
      start();
    } else {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          visible += entry.isIntersecting ? 1 : -1;
        });
        visible = Math.max(0, visible);
        if (visible) start();
      }, { threshold: 0 });

      items.forEach(function (item) { observer.observe(item.banner); });
    }

    window.addEventListener('resize', update);
    update();
  }

  function animateChaosFooterTransition() {
    if (typeof ScrollTrigger === 'undefined') return;

    document.querySelectorAll('.footer').forEach(function (footer) {
      var prev = footer.previousElementSibling;
      /* MUNCHIEF PORT: in the theme the footer sits inside its own <footer>
         element after <main>, so the chaos banner is not its DOM sibling any
         more. Fall back to the last chaos banner on the page, which is the
         one the footer's wave slides over. */
      if (!prev || !prev.classList.contains('chaos-banner')) {
        var banners = document.querySelectorAll('.chaos-banner');
        prev = banners.length ? banners[banners.length - 1] : null;
      }
      if (!prev) return;

      var content = prev.querySelector('.chaos-banner__content');
      if (!content) return;

      gsap.to(content, {
        yPercent: -18,
        ease: 'none',
        scrollTrigger: {
          trigger: prev,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true
        }
      });
    });
  }

  /* --------------------------------------------------------------------
     Chaos banner background parallax: the photo drifts top-to-bottom,
     scrubbed to how far the section has scrolled through the viewport
     (top bottom -> bottom top covers the section's whole time on screen,
     so this is already "live" right on load if the section starts in
     view, and keeps tracking as the visitor scrolls). The img is sized
     30% taller than its box in CSS (chaos-banner.css) so this travel
     never reveals an edge past the section's own overflow: hidden crop.
     -------------------------------------------------------------------- */
  function animateChaosBannerBackground() {
    if (typeof ScrollTrigger === 'undefined') return;

    document.querySelectorAll('.chaos-banner__media img').forEach(function (img) {
      gsap.fromTo(img,
        { yPercent: -10 },
        {
          yPercent: 10,
          ease: 'none',
          scrollTrigger: {
            trigger: img.closest('.chaos-banner'),
            start: 'top bottom',
            end: 'bottom top',
            scrub: true
          }
        }
      );
    });
  }

  /* --------------------------------------------------------------------
     Shop-now button group (.btn-group pairing a .btn--pill with a
     .btn--icon-circle, e.g. the hero's "Shop Now" + arrow CTA): on hover,
     the pair swaps sides — the icon slides to the pill's spot on the left,
     the pill slides to the icon's spot on the right — and the icon fills
     solid (see .btn--icon-circle.is-swapped in components.css) to read as
     the now-active control. Springs back apart on mouseleave. Distances
     are measured per pair (not hardcoded) so it works regardless of label
     length. Applies to every btn-group with both a pill and an
     icon-circle, not just the hero, since that pairing is the shared
     component used across sections.
     -------------------------------------------------------------------- */
  function initShopBtnGroupHover() {
    document.querySelectorAll('.btn-group').forEach(function (group) {
      var pill = group.querySelector('.btn--pill');
      var icon = group.querySelector('.btn--icon-circle');
      if (!pill || !icon) return;

      var pillRect = pill.getBoundingClientRect();
      var iconRect = icon.getBoundingClientRect();
      var iconShift = pillRect.left - iconRect.left;
      var pillShift = iconRect.right - pillRect.right;

      var pillTween = gsap.to(pill, { x: pillShift, duration: 0.4, ease: 'power3.inOut', paused: true });
      var iconTween = gsap.to(icon, { x: iconShift, duration: 0.4, ease: 'power3.inOut', paused: true });

      group.addEventListener('mouseenter', function () {
        pillTween.play();
        iconTween.play();
        icon.classList.add('is-swapped');
      });
      group.addEventListener('mouseleave', function () {
        pillTween.reverse();
        iconTween.reverse();
        icon.classList.remove('is-swapped');
      });
    });
  }

  /* --------------------------------------------------------------------
     World grid cards ("Crunch World" / "Prebiotic World"): each links to
     the matching product carousel further down the page (#munchief-family,
     #munchief-sodas) instead of navigating away. Intercepts the click and
     scrolls there smoothly — through Lenis if it's running (keeps the jump
     in the same eased scroll space as everything else), falling back to
     native smooth scrollIntoView otherwise — offset by the sticky header's
     current height so the target section's own heading isn't hidden under
     it on landing.
     -------------------------------------------------------------------- */
  function initWorldCardLinks() {
    document.querySelectorAll('.world-card[href^="#"]').forEach(function (card) {
      var id = card.getAttribute('href').slice(1);
      if (!id) return;

      card.addEventListener('click', function (e) {
        var target = document.getElementById(id);
        if (!target) return;

        e.preventDefault();

        var header = document.querySelector('.site-header');
        var offset = header ? header.getBoundingClientRect().height : 0;

        if (lenis) {
          lenis.scrollTo(target, { offset: -offset });
        } else {
          var scroller = scrollerElement();
          var y = target.getBoundingClientRect().top + scroller.scrollTop - offset;
          scroller.scrollTo({ top: y, behavior: 'smooth' });
        }
      });
    });
  }

  /* --------------------------------------------------------------------
     Mouse-parallax hover: while the cursor is over `container`, each
     entry's element drifts toward the cursor (higher strength = more
     drift, so entries read as sitting at slightly different depths),
     springing back to rest on mouseleave. gsap.quickTo instead of gsap.to
     since this fires on every mousemove and needs to be cheap. Shared by
     the hero side images, the footer's two cloud-photo bubbles, and the
     flavor quiz's three character cutouts. `entries` is an array of
     { el, strength }; entries with no el (or a falsy el) are skipped, so
     callers can pass a fixed-shape array even when an element is missing.
     -------------------------------------------------------------------- */
  function initParallaxHover(container, entries) {
    if (!container) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var movers = entries.filter(function (entry) {
      return entry && entry.el;
    }).map(function (entry) {
      return {
        x: gsap.quickTo(entry.el, 'x', { duration: 0.6, ease: 'power3.out' }),
        y: gsap.quickTo(entry.el, 'y', { duration: 0.6, ease: 'power3.out' }),
        strength: entry.strength
      };
    });
    if (!movers.length) return;

    container.addEventListener('mousemove', function (e) {
      var rect = container.getBoundingClientRect();
      var relX = (e.clientX - rect.left) / rect.width - 0.5;
      var relY = (e.clientY - rect.top) / rect.height - 0.5;

      movers.forEach(function (mover) {
        mover.x(relX * mover.strength);
        mover.y(relY * mover.strength * 0.6);
      });
    });

    container.addEventListener('mouseleave', function () {
      movers.forEach(function (mover) {
        mover.x(0);
        mover.y(0);
      });
    });
  }

  function initHeroParallax() {
    var hero = document.querySelector('.hero');
    if (!hero) return;
    initParallaxHover(hero, [
      { el: hero.querySelector('.hero__side--left'), strength: 26 },
      { el: hero.querySelector('.hero__side--right'), strength: 16 }
    ]);
  }

  /* --------------------------------------------------------------------
     Footer bubble parallax: same mechanic as the hero, applied to the two
     cloud-photo callouts (.footer__bubble--left/--right) — see
     initParallaxHover above. Smaller strengths since the bubbles are much
     smaller than the hero's side images.
     -------------------------------------------------------------------- */
  function initFooterBubbleParallax() {
    var footerHero = document.querySelector('.footer__hero');
    if (!footerHero) return;
    initParallaxHover(footerHero, [
      { el: footerHero.querySelector('.footer__bubble--left'), strength: 18 },
      { el: footerHero.querySelector('.footer__bubble--right'), strength: 14 }
    ]);
  }

  /* --------------------------------------------------------------------
     Flavor quiz character parallax: same mechanic as the hero, applied to
     the three decorative cutouts bleeding off the section's edges (see
     initParallaxHover above). tl is the largest/closest cutout so it gets
     the most drift; tr and bl are smaller and sit further back.
     -------------------------------------------------------------------- */
  function initFlavorQuizParallax() {
    var quiz = document.querySelector('.flavor-quiz');
    if (!quiz) return;
    initParallaxHover(quiz, [
      { el: quiz.querySelector('.flavor-quiz__character--tl'), strength: 22 },
      { el: quiz.querySelector('.flavor-quiz__character--tr'), strength: 16 },
      { el: quiz.querySelector('.flavor-quiz__character--bl'), strength: 14 }
    ]);
  }

  /* --------------------------------------------------------------------
     About-moments collage parallax: same mechanic as the hero, applied to
     the section's cloud cutouts (see initParallaxHover above).

     The cutouts are theme blocks with no positional class of their own, so
     depth comes from DOM order — which is what the section CSS places them
     by too: 1 is the large cutout in front, 2 the medium one over its upper
     left, 3 the small one furthest back, so the drift falls off the same
     way the sizes do. Any extra block gets the smallest drift.

     querySelectorAll rather than the querySelector the single-instance
     sections above use: about_moments ships as a preset, so a merchant can
     drop two of them on one page and each needs its own listener.
     -------------------------------------------------------------------- */
  function initMomentsCollageParallax() {
    var STRENGTHS = [24, 17, 12];

    document.querySelectorAll('.moments').forEach(function (section) {
      var photos = section.querySelectorAll('.moments__collage .cloud-photo');
      if (!photos.length) return;

      initParallaxHover(section, Array.prototype.map.call(photos, function (el, i) {
        return { el: el, strength: STRENGTHS[i] || 10 };
      }));
    });
  }

  /* --------------------------------------------------------------------
     About page — hero band.

     The headline already runs the shared character reveal (its
     data-animate="char-reveal" in the section markup) and the subtitle the
     generic fade-up, so this only handles the artwork:

       - the cast photo settles out of a slight zoom, which gives the band
         a beat of its own before the letters start landing, and
       - it drifts as the visitor scrolls past, so the sky reads as sitting
         behind the copy rather than printed on it.

     The drift is a scrubbed ScrollTrigger rather than the live-rect rAF
     the chaos banner uses: this band is the first thing on the page, so
     nothing below it can lazy-load and push its start position out from
     under it.

     The photo rests slightly over scale 1 so the drift never pulls an edge
     into the band, and `.band`'s overflow: hidden clips the overshoot.
     -------------------------------------------------------------------- */
  function animateAboutHero() {
    var hero = document.querySelector('.about-hero');
    if (!hero) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var photo = hero.querySelector('.band__media img');
    var seal = hero.querySelector('.about-hero__seal');

    if (photo) {
      /* 1.1 at rest against a 4% drift: half the 10% overscan sits at each
         edge, so the travel stays inside it. */
      gsap.fromTo(photo, { scale: 1.16 }, { scale: 1.1, duration: 1.6, ease: 'power2.out' });

      if (typeof ScrollTrigger !== 'undefined') {
        gsap.to(photo, {
          yPercent: 4,
          ease: 'none',
          scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true }
        });
      }
    }

    /* The seal is off by default (the shipped artwork has the badge drawn
       into it), so this only runs for a merchant who turned it back on.
       Its resting rotation is restated as the tween's end value rather than
       left to the stylesheet: gsap rewrites the whole transform, so a tween
       that omitted it would land the seal straight. */
    if (seal) {
      gsap.fromTo(seal,
        { scale: 0, rotation: -25, opacity: 0 },
        { scale: 1, rotation: 14.77, opacity: 1, duration: 0.7, delay: 0.4, ease: 'back.out(1.7)' }
      );
    }
  }

  /* --------------------------------------------------------------------
     About page — manifesto band.

     One timeline per band: the cut-out packshot swings in from the left
     edge it bleeds against, the statement wipes up behind it, and the
     sparkles pop last and then keep twinkling. The body copy is not here —
     it carries data-animate="word-reveal" in the section markup and runs
     through the shared hook.

     The statement is wiped with clip-path rather than split into character
     spans like every other title on the site. Its struck claim is a real
     <s>, and text-decoration does not paint across the inline-block word
     wrappers splitIntoChars produces — the split would silently cost the
     band the line through "healthy first, fun never." The clip-path is
     cleared on completion so the white text-stroke is never clipped at
     rest.

     Driven by an IntersectionObserver, not a ScrollTrigger, for the reason
     spelled out on revealChars: the band sits below the lazy-loaded hero
     art, so a start position measured at DOMContentLoaded is stale by the
     time the reader arrives.

     The packshot animates on the <img>, not on `.manifesto__media` — the
     wrapper is centred with a translateY(-50%) that a gsap transform would
     overwrite, dropping the artwork half its height down the band.
     -------------------------------------------------------------------- */
  function animateAboutManifesto() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    /* querySelectorAll, not querySelector: the section ships as a preset,
       so a merchant can place two manifesto bands on one page. */
    document.querySelectorAll('.manifesto').forEach(function (band) {
      var packshot = band.querySelector('.manifesto__media img');
      var statement = band.querySelector('.manifesto__statement');
      var sparkles = Array.prototype.slice.call(band.querySelectorAll('.band__sparkle'));

      if (!packshot && !statement && !sparkles.length) return;

      var tl = gsap.timeline({ paused: true, onComplete: settle });

      if (packshot) {
        tl.fromTo(packshot,
          { opacity: 0, x: -90, rotation: -8, scale: 0.86 },
          { opacity: 1, x: 0, rotation: 0, scale: 1, duration: 0.9, ease: 'power3.out' },
          0
        );
      }

      if (statement) {
        tl.fromTo(statement,
          { opacity: 0, y: 26, clipPath: 'inset(0 0 100% 0)' },
          {
            opacity: 1,
            y: 0,
            clipPath: 'inset(0 0 0% 0)',
            duration: 0.9,
            ease: 'power3.out',
            clearProps: 'clipPath'
          },
          0.12
        );
      }

      if (sparkles.length) {
        tl.fromTo(sparkles,
          { opacity: 0, scale: 0, rotation: -90 },
          { opacity: 1, scale: 1, rotation: 0, duration: 0.5, stagger: 0.09, ease: 'back.out(2)' },
          0.35
        );
      }

      /* Both idle behaviours start only once the entrance has landed, so
         neither is fighting the timeline for the same properties. */
      function settle() {
        sparkles.forEach(function (sparkle, i) {
          gsap.to(sparkle, {
            scale: 0.75,
            opacity: 0.6,
            duration: 1.4 + i * 0.2,
            delay: i * 0.25,
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut'
          });
        });

        /* Mouse parallax over the whole band — see initParallaxHover. The
           packshot leads and the sparkles trail it, which is the same
           depth order the comp draws them in. */
        initParallaxHover(band, [{ el: packshot, strength: 26 }].concat(
          sparkles.map(function (sparkle) {
            return { el: sparkle, strength: 12 };
          })
        ));
      }

      if (typeof IntersectionObserver === 'undefined') {
        tl.play();
        return;
      }

      new IntersectionObserver(function (entries, obs) {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        tl.play();
      }, { threshold: 0.2 }).observe(band);
    });
  }

  /* --------------------------------------------------------------------
     About page — world bands ("Crunch World", "Prebiotic World").

     The copy runs on the shared data-animate hooks in the section markup
     (char-reveal heading, word-reveal blurb, staggered chips, fade-up
     CTA); this only drives the scene photo, which slides slowly against
     the scroll so the band reads as depth rather than as a flat backdrop
     behind the type.

     A live-rect rAF loop, not a ScrollTrigger scrub — the same call the
     chaos banner makes, for the same reason: both bands sit well down a
     page whose height keeps changing as the images above them load, so a
     start position stored once goes stale and the scrub silently stops
     tracking. Reading the rect every frame cannot.

     The photo is held at scale 1.08 so the 3% travel each way stays inside
     its own overscan and never pulls a band edge into view; `.band`'s
     overflow: hidden clips the rest. Written straight to style.transform
     rather than through gsap — the element carries no transform of its own,
     and this is a per-frame write.
     -------------------------------------------------------------------- */
  function animateWorldBands() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var bands = Array.prototype.slice.call(document.querySelectorAll('.world-band'))
      .map(function (band) {
        return { band: band, image: band.querySelector('.band__media img') };
      })
      .filter(function (item) { return item.image; });
    if (!bands.length) return;

    var TRAVEL = 3;

    function update() {
      var viewport = window.innerHeight;

      bands.forEach(function (item) {
        var rect = item.band.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > viewport) return;

        /* 0 as the band's top meets the bottom of the viewport, 1 as its
           bottom leaves the top — so the photo travels its whole range over
           exactly the time the band is on screen. */
        var progress = (viewport - rect.top) / (rect.height + viewport);
        progress = Math.min(1, Math.max(0, progress));

        item.image.style.transform =
          'translateY(' + (TRAVEL - progress * TRAVEL * 2).toFixed(2) + '%) scale(1.08)';
      });
    }

    /* Frame loop rather than a scroll listener, and idle while every band is
       off screen — see initChaosScrollScrub for why scroll events are not
       enough under Lenis' virtual scroller. */
    var visible = 0;
    var running = false;

    function frame() {
      if (!visible) {
        running = false;
        return;
      }
      update();
      requestAnimationFrame(frame);
    }

    function start() {
      if (running) return;
      running = true;
      requestAnimationFrame(frame);
    }

    if (typeof IntersectionObserver === 'undefined') {
      visible = bands.length;
      start();
    } else {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          visible += entry.isIntersecting ? 1 : -1;
        });
        visible = Math.max(0, visible);
        if (visible) start();
      }, { threshold: 0 });

      bands.forEach(function (item) { observer.observe(item.band); });
    }

    window.addEventListener('resize', update);
    update();
  }

  /* MUNCHIEF PORT: reveal safety net.

     Every animation above hides its target first and waits for a
     ScrollTrigger whose start position was measured when it was created. The
     theme lazy-loads images, so the page keeps growing as the visitor scrolls
     and those positions drift — the footer's hero stayed invisible even once
     it was fully on screen. This watches everything that starts hidden and
     shows anything that is genuinely visible but still transparent, so a
     mis-measured trigger can never cost the visitor the content. */
  function revealStuck() {
    if (typeof IntersectionObserver === 'undefined') return;

    var selectors = [
      '[data-animate]',
      '.hero__side',
      '.hero__text',
      '.hero .btn-group',
      '.brand-story__heading',
      '.brand-story__photo',
      '.brand-story__characters',
      '.brand-story__subheading',
      '.brand-story__text',
      '.brand-story .btn-group'
    ];

    var candidates = [];
    document.querySelectorAll(selectors.join(',')).forEach(function (el) {
      candidates.push(el);
      Array.prototype.push.apply(candidates, expandBlocks(el.children));
    });
    /* Character spans are excluded on purpose. Their reveal is a long
       deliberate stagger (revealChars above), so at this net's 400ms mark
       most of the line is still legitimately at opacity 0 — it would read
       them as stuck and overwrite the whole stagger with one flat fade.
       They can't get stuck anyway: that reveal is observer-driven, not
       ScrollTrigger-driven. They arrive here as children of a
       [data-animate="char-reveal"] heading, so filtering is needed on top
       of dropping them from the selector list. */
    candidates = candidates.filter(function (el) {
      if (!el.classList) return true;
      /* Word wrappers are excluded for the same reason as the characters:
         data-animate="word-reveal" starts them transparent and walks through
         them on an observer, so at the 400ms mark most of a paragraph is
         still legitimately hidden and the net would overwrite the sequence
         with one flat fade. */
      return !el.classList.contains('word-reveal__char') &&
        !el.classList.contains('word-reveal__word');
    });
    if (!candidates.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        observer.unobserve(el);

        /* Give the real animation a moment to run first — this only steps in
           when the element is on screen and still invisible. A staggered card
           carries its own start offset (set in animateOnScroll), so the wait
           clears the whole sequence rather than cutting it short. */
        var wait = 400 + (parseFloat(el.dataset && el.dataset.revealDelay) || 0);
        setTimeout(function () {
          if (!el.isConnected) return;
          if (parseFloat(getComputedStyle(el).opacity) > 0.01) return;
          /* scale:1 as well as x/y — word-reveal spans start scaled down,
             so restoring opacity alone would leave them tiny. */
          gsap.to(el, { opacity: 1, x: 0, y: 0, scale: 1, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
        }, wait);
      });
    }, { threshold: 0 });

    candidates.forEach(function (el) { observer.observe(el); });
  }

  /* MUNCHIEF PORT: start at the top.

     Browsers restore the previous scroll offset on reload, and this page's
     height keeps changing as lazy images and the reveal animations resolve —
     so a restored offset lands somewhere in the middle, typically a couple of
     sections down. The static build never had this: every page was a fresh
     document that opened at the top.

     A real deep link (#reviews, #munchief-family) still wins; this only
     applies when there is no hash. */
  function startAtTop() {
    if (!document.querySelector('.munchief')) return;
    if (window.location.hash) return;

    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    function toTop() {
      if (window.location.hash) return;
      if (lenis) {
        lenis.scrollTo(0, { immediate: true });
      } else {
        scrollerElement().scrollTo(0, 0);
      }
    }

    toTop();
    /* Late images and fonts shift the layout after first paint — hold the top
       until the page has actually finished loading. */
    window.addEventListener('load', toTop);
  }

  /* --------------------------------------------------------------------
     Drag-to-scroll for horizontal card tracks.

     A trackpad or touchscreen can already scroll these sideways, but a mouse
     cannot: there is no scrollbar (hidden, by design — the peeking card is
     the affordance) and a wheel only scrolls the page. So the track becomes
     grabbable.

     Mouse only. Touch and pen already pan natively, and hijacking those would
     replace a good native behaviour with a worse imitation.

     Two details that make it feel like dragging rather than like scripting:

     Snap is switched off for the duration (via .is-dragging) so the track
     follows the pointer exactly instead of fighting it, then restored on
     release so the throw still settles neatly against a card.

     Release throws the track rather than stopping dead. Velocity is measured
     over the last few moves and projected forward, which is what makes a
     flick feel weighted. Clamped to the track's own scroll range so it never
     fights the end stops.
     -------------------------------------------------------------------- */
  function initDragScroll() {
    var tracks = document.querySelectorAll('[data-munchief-drag-scroll]');
    if (!tracks.length) return;

    Array.prototype.forEach.call(tracks, function (track) {
      var dragging = false;
      var startX = 0;
      var startScroll = 0;
      var moved = 0;
      var captured = false;  /* pointer capture is taken only once a drag is real */
      var lastX = 0;
      var lastT = 0;
      var velocity = 0;   /* px per ms, sign follows scrollLeft */

      function onDown(e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;

        /* Let real controls inside a card keep their own behaviour. Links are
           NOT in that list: a card is usually one big <a>, so bailing on
           anchors meant the only draggable part of a rail of link-cards was
           the gap between them — the throw this whole handler exists for was
           unreachable. The capture-phase click guard below already stops a
           drag that ends on a card from also navigating. */
        if (e.target.closest('button, input, select, textarea')) return;

        gsap.killTweensOf(track);
        dragging = true;
        moved = 0;
        startX = lastX = e.clientX;
        startScroll = track.scrollLeft;
        lastT = e.timeStamp;
        velocity = 0;
        track.classList.add('is-dragging');
        captured = false;
      }

      function onMove(e) {
        if (!dragging) return;
        var dx = e.clientX - startX;
        moved = Math.max(moved, Math.abs(dx));

        /* Captured only once the pointer has actually travelled, NOT on
           pointerdown. While a capture is active the click that follows is
           retargeted to the capturing element, so capturing up front meant a
           plain click on a card was delivered to the track and the card's own
           <a> never saw it — the rail became unclickable the moment cards
           were allowed to start a drag. Below the threshold nothing is
           captured and the click reaches the link untouched. */
        if (!captured && moved > 6) {
          track.setPointerCapture(e.pointerId);
          captured = true;
        }

        track.scrollLeft = startScroll - dx;

        var dt = e.timeStamp - lastT;
        if (dt > 0) {
          /* Smooth the estimate so one jittery sample cannot define the throw. */
          velocity = 0.8 * ((lastX - e.clientX) / dt) + 0.2 * velocity;
          lastX = e.clientX;
          lastT = e.timeStamp;
        }
        e.preventDefault();
      }

      function onUp(e) {
        if (!dragging) return;
        dragging = false;
        track.classList.remove('is-dragging');
        if (captured && track.hasPointerCapture(e.pointerId)) {
          track.releasePointerCapture(e.pointerId);
        }
        captured = false;

        /* A stale velocity from a drag that ended in a pause should not throw. */
        if (e.timeStamp - lastT > 90) velocity = 0;

        var throwBy = velocity * 190;
        if (Math.abs(throwBy) > 12) {
          var limit = track.scrollWidth - track.clientWidth;
          var target = Math.max(0, Math.min(limit, track.scrollLeft + throwBy));
          gsap.to(track, {
            scrollLeft: target,
            duration: Math.min(0.9, 0.28 + Math.abs(throwBy) / 1400),
            ease: 'power2.out',
            overwrite: true
          });
        }
      }

      /* A drag that ends on a card must not also read as a click on it. */
      track.addEventListener('click', function (e) {
        if (moved > 6) { e.preventDefault(); e.stopPropagation(); }
      }, true);

      track.addEventListener('dragstart', function (e) { e.preventDefault(); });
      track.addEventListener('pointerdown', onDown);
      track.addEventListener('pointermove', onMove);
      track.addEventListener('pointerup', onUp);
      track.addEventListener('pointercancel', onUp);
    });
  }

  /* --------------------------------------------------------------------
     Section wave edges: a rolling sea surface.

     The terracotta edges around the story band are inline SVGs (see the
     markup in sections/munchief-product-story.liquid and
     sections/munchief-product-photo-banner.liquid) rather than CSS masks, so
     their paths can be re-shaped rather than merely moved.

     These are Gerstner (trochoidal) waves, not plain sine curves. A sine has
     crests and troughs of the same shape, which is exactly what open water
     does NOT look like: a real swell has sharp, narrow crests and broad flat
     troughs. Gerstner gets that by moving each surface point in a circle
     rather than straight up and down — every sample is displaced sideways as
     well as vertically, which bunches points together at the crests and
     spreads them out through the troughs.

     Four wave trains are summed, from a long slow swell down to short
     ripples. Their wavelengths and speeds share no common factor, so the
     surface never repeats — that is what stops it reading as a loop.

     STEEPNESS scales the sideways displacement. At 0 these collapse back to
     plain sines; at 1 the crests cusp and the surface folds over itself. The
     value below keeps the crests crisp while staying well clear of folding.

     Because points move sideways, the samples no longer land on tidy x
     positions and the span no longer starts at 0 or ends at WIDTH. So the
     surface is sampled wider than the band and then clipped back to it, with
     the two edge points interpolated — otherwise the wave would part company
     with the section's corners as it drifts.

     Geometry is declared per element, in the markup, so this function stays
     section-agnostic:
       data-munchief-wave           marks the <svg>
       data-wave-fill="below|above" which side of the surface is solid
       data-wave-floor              the viewBox height
       data-wave-mid                the surface's resting level
       data-wave-phase              phase offset, so two edges on one page
                                    never swing in lockstep
     and each <path> inside carries data-wave-offset — how far below the
     surface that path sits, which is how the photo banner draws the thin
     cream fringe hugging the underside of its edge.

     After changing WAVE, STEEPNESS or the markup's mid/floor, regenerate
     every static `d` in the markup so the no-JS state still matches.
     -------------------------------------------------------------------- */
  function initSectionWaves() {
    var waves = document.querySelectorAll('[data-munchief-wave]');
    if (!waves.length) return;

    /* Reduced motion keeps the authored rest surfaces, untouched. */
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var WIDTH = 1440;
    var SAMPLES = 100;
    var PAD = 220;        /* comfortably more than the max sideways travel */
    /* Low on purpose. Steepness is what cusps the crests into sharp peaks;
       the reference edge (brand_story's tiling wave) is a plain rounded
       swell, so this stays near-sinusoidal — enough to keep the crests from
       looking mechanically symmetric, not enough to sharpen them. */
    var STEEPNESS = 0.3;

    /* Three trains, no high-frequency ripple: a fourth short train is what
       made the surface read as chop rather than a rolling swell. Reach totals
       77 against a mid of 112, so the surface stays inside 0..floor with room
       for the 4px fringe offset. Crest counts share no common factor, which
       keeps the summed surface from repeating; together they put 5-6 even,
       rounded undulations across the 1440 band, matching the reference edge's
       rhythm. Speeds are ~2.5x the previous set — one wavelength every 7s at
       the swell, which reads as motion rather than a slow crawl. */
    var WAVE = [
      { reach: 44, crests: 1.6, phase: 0.0, speed: 1 / 7 },     /* primary swell */
      { reach: 22, crests: 2.9, phase: 1.7, speed: -1 / 5 },    /* secondary, crossing */
    ];

    WAVE.forEach(function (w) { w.k = (Math.PI * 2 * w.crests) / WIDTH; });

    /* One surface point. `u` is the water particle's rest position; the point
       it actually occupies is displaced from it on both axes. */
    function surfaceAt(u, t, cfg, offset) {
      var x = u;
      var y = cfg.mid + offset;
      for (var i = 0; i < WAVE.length; i++) {
        var w = WAVE[i];
        var theta = w.k * u + Math.PI * 2 * w.speed * t + w.phase + cfg.phase;
        x += STEEPNESS * w.reach * Math.cos(theta);
        y -= w.reach * Math.sin(theta);
      }
      /* mid plus the combined reach stays inside 0..floor, so this is a guard
         against future retuning, not something that fires today. */
      if (y < 0) y = 0; else if (y > cfg.floor) y = cfg.floor;
      return [x, y];
    }

    function yBetween(a, b, x) {
      var span = b[0] - a[0];
      return span === 0 ? a[1] : a[1] + (b[1] - a[1]) * ((x - a[0]) / span);
    }

    function buildPath(t, cfg, offset) {
      var raw = [];
      for (var i = 0; i <= SAMPLES; i++) {
        var u = -PAD + (i / SAMPLES) * (WIDTH + PAD * 2);
        raw.push(surfaceAt(u, t, cfg, offset));
      }

      /* Clip the oversampled span back to the band, interpolating the two
         points that land exactly on its edges. */
      var pts = [];
      for (var j = 1; j < raw.length; j++) {
        var a = raw[j - 1], b = raw[j];
        if (b[0] < 0 || a[0] > WIDTH) continue;
        if (a[0] < 0 && b[0] >= 0) pts.push([0, yBetween(a, b, 0)]);
        if (b[0] >= 0 && b[0] <= WIDTH) pts.push(b);
        if (a[0] <= WIDTH && b[0] > WIDTH) pts.push([WIDTH, yBetween(a, b, WIDTH)]);
      }
      if (pts.length < 3) return '';

      /* Quadratic midpoint smoothing: each sample becomes a control point and
         the curve passes through the midpoints between them, which is
         C1-continuous, so no facets show even at the crests. */
      var d = 'M' + pts[0][0].toFixed(2) + ',' + pts[0][1].toFixed(2);
      for (var m = 1; m < pts.length - 1; m++) {
        var mx = (pts[m][0] + pts[m + 1][0]) / 2;
        var my = (pts[m][1] + pts[m + 1][1]) / 2;
        d += 'Q' + pts[m][0].toFixed(2) + ',' + pts[m][1].toFixed(2) +
             ' ' + mx.toFixed(2) + ',' + my.toFixed(2);
      }
      var last = pts[pts.length - 1];
      d += 'L' + last[0].toFixed(2) + ',' + last[1].toFixed(2);

      /* Close along whichever edge is solid — the floor for a wave sitting
         above its section, the top for one hanging below it. */
      var edge = cfg.fillAbove ? 0 : cfg.floor;
      return d + 'L' + WIDTH + ',' + edge + 'L0,' + edge + 'Z';
    }

    Array.prototype.forEach.call(waves, function (svg) {
      var paths = svg.querySelectorAll('path');
      if (!paths.length) return;

      var cfg = {
        floor: parseFloat(svg.getAttribute('data-wave-floor')) || 240,
        mid: parseFloat(svg.getAttribute('data-wave-mid')) || 112,
        phase: parseFloat(svg.getAttribute('data-wave-phase')) || 0,
        fillAbove: svg.getAttribute('data-wave-fill') === 'above'
      };

      var layers = Array.prototype.map.call(paths, function (path) {
        return { path: path, offset: parseFloat(path.getAttribute('data-wave-offset')) || 0 };
      });

      /* Clock from first frame, not from page load, so the animation starts
         on the same t=0 surface the markup already shows. */
      var origin = null;

      var tick = function () {
        if (origin === null) origin = gsap.ticker.time;
        var t = gsap.ticker.time - origin;
        for (var i = 0; i < layers.length; i++) {
          var d = buildPath(t, cfg, layers[i].offset);
          if (d) layers[i].path.setAttribute('d', d);
        }
      };

      /* Only drive frames while the edge is actually on screen. */
      if (typeof IntersectionObserver === 'undefined') {
        gsap.ticker.add(tick);
        return;
      }

      var running = false;
      new IntersectionObserver(function (entries) {
        var visible = entries[0].isIntersecting;
        if (visible === running) return;
        running = visible;
        if (visible) gsap.ticker.add(tick); else gsap.ticker.remove(tick);
      }, { rootMargin: '120px 0px' }).observe(svg);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    startAtTop();

    /* MUNCHIEF PORT: initPageTransitions() is deliberately not called. It
       appends its curtain to <body>, outside the .munchief wrapper this
       build's CSS is namespaced under, so the overlay would be invisible
       while still delaying every link click by its animation duration. */
    initSmoothScroll();
    animateHeroHeading();
    animateHeroSideImages();
    animateBrandStoryIntro();
    animateBrandStoryAbout();
    initSectionWaves();
    initDragScroll();
    animateOnScroll();
    initChaosScrollScrub();
    animateFooterHeading();
    initShopBtnGroupHover();
    initWorldCardLinks();
    initHeroParallax();
    initFooterBubbleParallax();
    initFlavorQuizParallax();
    initMomentsCollageParallax();
    animateAboutHero();
    animateAboutManifesto();
    animateWorldBands();

    /* MUNCHIEF PORT: the theme lazy-loads images below the fold, so the page
       grows after ScrollTrigger has stored each trigger's start position and
       those positions go stale — sections further down then never animate
       in. Re-measure on load and whenever a late image arrives. */
    revealStuck();

    if (typeof ScrollTrigger !== 'undefined') {
      var refreshPending;
      var refresh = function () {
        clearTimeout(refreshPending);
        refreshPending = setTimeout(function () { ScrollTrigger.refresh(); }, 120);
      };

      window.addEventListener('load', refresh);
      document.querySelectorAll('img').forEach(function (img) {
        if (!img.complete) img.addEventListener('load', refresh, { once: true });
      });
    }
  });
})();
